/**
 * Note Top Bar Edit Component
 *
 * Top bar for note editing mode
 * - Left section: Template dropdown, edit template button
 * - Right section: Copy sections button
 * - Menu: Undo/redo, settings, find/replace, delete
 *
 * Uses TopBarLayout for consistent structure across view modes
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type TemplatePickerItem } from '@components/Pickers/TemplatePicker';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { useDeleteTranscriptsMutation } from '@features/encounters';
import { notesApi, type NoteSection, useEditTemplateLabelExperiment } from '@features/notes';
import { useContainerQuery } from '@hooks/useContainerQuery';
import { EditIcon } from '@icons/EditIcon';
import { PenToolIcon } from '@icons/PenToolIcon';
import {
  ArrowDropDown as ArrowDropDownIcon,
  AutoAwesome as AutoAwesomeIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
  Deselect as DeselectIcon,
  FindReplace as FindReplaceIcon,
  Settings as SettingsIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEventTracking, useFeatureGate } from '@services/statsig';
import type { EncounterResponse } from '@store/generated/encounters';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { LAYOUT_SELECTORS } from '../constants/layout';
import { useTemplateSwitch } from '../hooks/useTemplateSwitch';
import { clearCopySectionSelection, toggleChatVisibility } from '../store/chatSlice';
import { selectIsChatVisible } from '../store/selectors';

import type { MenuItemConfig } from './layouts/TopBarLayout';
import { TopBarLayout } from './layouts/TopBarLayout';
import { NoteTOCButton } from './NoteTOC/NoteTOCButton';
import { TemplateDropdown } from './TemplateDropdown';
import { TemplateSwitchModal } from './TemplateSwitchModal';

export interface NoteTopBarEditProps {
  /**
   * Note ID for context
   */
  noteId: string;

  /**
   * Current template ID
   */
  currentTemplateId?: string;

  /**
   * Current template name
   */
  currentTemplateName?: string;

  /**
   * Encounter ID for analytics
   */
  encounterId?: string;

  /**
   * Whether user has permission to edit template
   */
  canEditTemplate?: boolean;

  /**
   * Whether the note can be regenerated (false when transcripts deleted)
   */
  canRegenerate?: boolean;

  /**
   * Number of sections in the note (for analytics)
   */
  sectionsCount?: number;

  /**
   * Number of sections selected for copying
   */
  selectedSectionsCount?: number;

  /**
   * Array of note sections (for TOC button)
   */
  sections?: NoteSection[];

  /**
   * CSS selector for scroll container (for TOC button)
   * @default LAYOUT_SELECTORS.NOTE_SECTIONS_CONTAINER
   */
  containerSelector?: string;

  /**
   * Handler for deleting the note
   */
  onDeleteNote?: () => Promise<void>;

  /**
   * Handler for opening Find & Replace dialog
   */
  onFindReplace?: () => void;

  /**
   * Handler for opening Copy/Paste Settings dialog
   */
  onCopyPasteSettings?: () => void;

  /**
   * Handler for copying all sections
   */
  onCopyAllSections?: () => void;

  /**
   * Handler for copying selected sections
   */
  onCopySelectedSections?: () => void;

  /**
   * Handler for Sign Note / Complete Note action
   */
  onSignNote?: () => void;

  /**
   * Whether user has note_signing feature flag
   */
  hasNoteSigning?: boolean;

  /**
   * Whether note has a patient assigned
   */
  hasPatient?: boolean;

  /**
   * Whether signing is in progress
   */
  isSigning?: boolean;

  /**
   * Callback when user clicks Sign/Review button — parent opens the billing popover
   */
  onOpenBillingPopover?: (anchorEl: HTMLElement) => void;

  /**
   * Encounter data for delete transcripts feature
   */
  encounterData?: EncounterResponse;

  /**
   * Number of audit issues to display as badge (when sidebar is closed)
   */
  auditIssueCount?: number;

  /**
   * Whether audit features are enabled
   */
  isAuditEnabled?: boolean;
}

/**
 * Top bar for note editing interface
 * Uses TopBarLayout with left/right/menuItems slots
 */
export const NoteTopBarEdit: React.FC<NoteTopBarEditProps> = ({
  noteId,
  currentTemplateId,
  currentTemplateName,
  encounterId,
  canEditTemplate,
  canRegenerate = true,
  sectionsCount,
  selectedSectionsCount = 0,
  sections = [],
  containerSelector = LAYOUT_SELECTORS.NOTE_SECTIONS_CONTAINER,
  onDeleteNote,
  onFindReplace,
  onCopyPasteSettings,
  onCopyAllSections,
  onCopySelectedSections,
  onSignNote,
  hasNoteSigning = false,
  hasPatient = false,
  isSigning = false,
  onOpenBillingPopover,
  encounterData,
  auditIssueCount = 0,
  isAuditEnabled = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { note: noteMetrics, noteSection, encounter: encounterTracking } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [templateSwitchModalOpen, setTemplateSwitchModalOpen] = useState(false);
  // Delete all transcripts state
  const [deleteAllTranscriptsDialogOpen, setDeleteAllTranscriptsDialogOpen] = useState(false);
  const [isDeletingAllTranscripts, setIsDeletingAllTranscripts] = useState(false);
  const [deleteTranscripts] = useDeleteTranscriptsMutation();

  // Responsive breakpoints for container queries
  const { ref, breakpoint } = useContainerQuery<HTMLDivElement>({
    xs: { maxWidth: 480 },
    sm: { minWidth: 481, maxWidth: 768 },
    md: { minWidth: 769, maxWidth: 1024 },
    lg: { minWidth: 1025 },
  });

  // Feature gates and experiments
  const showEditTemplate = useFeatureGate('feature_edit_template_from_note');
  const editButtonLabel = useEditTemplateLabelExperiment();

  // Show Edit Template inline only on lg breakpoint, otherwise move to menu
  const showEditTemplateInline = breakpoint === 'lg';

  // Show Copy button inline only on md and lg breakpoints, otherwise move to menu
  const showCopyButtonInline = breakpoint === 'md' || breakpoint === 'lg';

  // Show Template Dropdown inline only on md and lg breakpoints, otherwise show modal
  const showTemplateDropdownInline = breakpoint === 'md' || breakpoint === 'lg';

  // Show TOC button on xs/sm/md breakpoints (when inline TOC is hidden)
  const showTOCButton = breakpoint !== 'lg' && breakpoint !== 'md';

  // Redux state and dispatch
  const dispatch = useAppDispatch();
  const isChatVisible = useAppSelector(selectIsChatVisible);

  // Template switching logic
  const {
    selectedTemplate,
    isRegenerating,
    showConfirmDialog,
    currentTemplate,
    handleTemplateSelect,
    handleConfirmRegenerate,
    handleCancelRegenerate,
  } = useTemplateSwitch({
    noteId,
    currentTemplateId,
    currentTemplateName,
    encounterId,
  });

  const handleDeleteClick = useCallback(() => {
    noteMetrics.topbarDeleteClicked(noteId, {
      view_mode: 'edit',
      is_signed: false,
      encounter_id: encounterId,
    });
    setDeleteDialogOpen(true);
  }, [noteMetrics, noteId, encounterId]);

  const handleMenuOpen = useCallback(() => {
    noteMetrics.topbarMenuOpened(noteId, {
      view_mode: 'edit',
      encounter_id: encounterId,
    });
  }, [noteMetrics, noteId, encounterId]);

  // Delete all transcripts handlers
  const handleDeleteAllTranscriptsClick = useCallback(() => {
    if (!encounterId) return;
    encounterTracking.deleteAllTranscriptsClicked({
      encounter_id: encounterId,
      dictation_count: encounterData?.inputs?.length ?? 0,
    });
    setDeleteAllTranscriptsDialogOpen(true);
  }, [encounterId, encounterTracking, encounterData?.inputs?.length]);

  const handleConfirmDeleteAllTranscripts = useCallback(async () => {
    if (!encounterId) return;
    encounterTracking.deleteAllTranscriptsConfirmed({
      encounter_id: encounterId,
      dictation_count: encounterData?.inputs?.length ?? 0,
    });
    setIsDeletingAllTranscripts(true);
    try {
      const result = await deleteTranscripts({ encounterId }).unwrap();
      encounterTracking.deleteAllTranscriptsCompleted({
        encounter_id: encounterId,
        transcription_sessions_deleted: result.transcription_sessions_deleted ?? 0,
        notes_cleared: result.notes_cleared ?? 0,
      });
      // Invalidate the current note cache to update can_regenerate
      if (noteId) {
        dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
      }
      enqueueSnackbar('Transcripts deleted successfully', { variant: 'success' });
      setDeleteAllTranscriptsDialogOpen(false);
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to delete transcripts';
      encounterTracking.deleteAllTranscriptsFailed({
        encounter_id: encounterId,
        error_message: errorMessage,
      });
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsDeletingAllTranscripts(false);
    }
  }, [encounterId, encounterTracking, encounterData?.inputs?.length, deleteTranscripts, enqueueSnackbar, noteId, dispatch]);

  const handleCancelDeleteAllTranscripts = useCallback(() => {
    if (encounterId) {
      encounterTracking.deleteAllTranscriptsCancelled({ encounter_id: encounterId });
    }
    setDeleteAllTranscriptsDialogOpen(false);
  }, [encounterId, encounterTracking]);

  const handleDeleteConfirm = async () => {
    if (!onDeleteNote) return;

    setIsDeleting(true);
    try {
      await onDeleteNote();
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete note:', error);
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleToggleChat = () => {
    dispatch(toggleChatVisibility());
    noteMetrics.topbarChatToggleClicked(noteId, {
      chat_visible: !isChatVisible,
      encounter_id: encounterId,
    });
  };

  const handleDeselectAll = () => {
    noteSection.deselectAllClicked({
      source: 'topbar',
      selected_count: selectedSectionsCount,
      note_id: noteId,
      encounter_id: encounterId,
    });
    dispatch(clearCopySectionSelection());
  };

  const handleEditTemplateClick = useCallback(() => {
    // Track intent to edit template
    noteMetrics.templateEditClicked(noteId, {
      template_id: currentTemplateId,
      template_name: currentTemplateName,
      source: 'note-top-bar',
      section_count: sectionsCount || 0,
    });
    
    // Navigate to templates page with template ID to open editor
    if (currentTemplateId) {
      navigate(`/templates?id=${currentTemplateId}`);
    } else {
      navigate('/templates');
    }
  }, [noteMetrics, noteId, currentTemplateId, currentTemplateName, sectionsCount, navigate]);

  const handleSwitchTemplateClick = () => {
    setTemplateSwitchModalOpen(true);
  };

  const handleTemplateSwitchModalClose = () => {
    setTemplateSwitchModalOpen(false);
  };

  const handleTemplateSelectFromModal = (template: TemplatePickerItem) => {
    // Use the existing handleTemplateSelect from useTemplateSwitch hook
    handleTemplateSelect(template);
    // Modal will be closed by the confirmation dialog or after regeneration
    setTemplateSwitchModalOpen(false);
  };

  // Build menu items configuration
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      // {
      //   label: 'Undo',
      //   icon: <UndoIcon fontSize="small" />,
      //   onClick: () => {
      //     // TODO: Implement undo functionality
      //   },
      //   disabled: true,
      //   shortcut: '⌘Z',
      // },
      // {
      //   label: 'Redo',
      //   icon: <RedoIcon fontSize="small" />,
      //   onClick: () => {
      //     // TODO: Implement redo functionality
      //   },
      //   disabled: true,
      //   shortcut: '⌘⇧Z',
      // },
    ];

    // Add Switch Template to menu when dropdown not showing inline (xs/sm breakpoints)
    if (!showTemplateDropdownInline) {
      items.push({
        label: canRegenerate === false ? 'Switch Template (Disabled)' : 'Switch Template',
        icon: <SwapHorizIcon fontSize="small" />,
        onClick: handleSwitchTemplateClick,
        disabled: canRegenerate === false,
      });
    }

    // Add Copy button to menu when not showing inline (xs/sm breakpoints)
    if (!showCopyButtonInline) {
      if (selectedSectionsCount === 0) {
        // Copy All Sections
        if (onCopyAllSections) {
          items.push({
            label: 'Copy All Sections',
            icon: <ContentCopyIcon fontSize="small" />,
            onClick: onCopyAllSections,
          });
        }
      } else {
        // Copy Selected
        if (onCopySelectedSections) {
          items.push({
            label: `Copy Selected (${selectedSectionsCount})`,
            icon: <ContentCopyIcon fontSize="small" />,
            onClick: onCopySelectedSections,
          });
        }
      }
    }

    // Add Edit Template to menu when not showing inline (breakpoint < lg)
    if (showEditTemplate && canEditTemplate === true && !showEditTemplateInline) {
      items.push({
        label: editButtonLabel,
        icon: <EditIcon fontSize="small" />,
        onClick: handleEditTemplateClick,
      });
    }

    // Add remaining menu items
    items.push(
      {
        label: 'Find & Replace',
        icon: <FindReplaceIcon fontSize="small" />,
        onClick: () => {
          noteMetrics.topbarFindReplaceClicked(noteId, {
            encounter_id: encounterId,
          });
          onFindReplace?.();
        },
        disabled: !onFindReplace,
      },
      {
        label: 'Copy/Paste Settings',
        icon: <SettingsIcon fontSize="small" />,
        onClick: () => {
          noteMetrics.topbarCopyPasteSettingsClicked(noteId, {
            encounter_id: encounterId,
          });
          onCopyPasteSettings?.();
        },
        disabled: !onCopyPasteSettings,
      },
      {
        label: 'Delete Note',
        icon: <DeleteIcon fontSize="small" />,
        onClick: handleDeleteClick,
        disabled: !onDeleteNote,
        divider: encounterData?.can_delete_transcripts !== true,
      },
    );

    // Add Delete Transcripts option when available (and not already deleted)
    if (encounterData?.can_delete_transcripts === true && encounterData?.transcripts_deleted !== true) {
      const transcriptCount = encounterData?.inputs?.length ?? 0;
      items.push({
        label: transcriptCount === 1 ? 'Delete Transcript' : 'Delete Transcripts',
        icon: <DeleteForeverIcon fontSize="small" color="error" />,
        onClick: handleDeleteAllTranscriptsClick,
        divider: true,
      });
    }

    return items;
  }, [
    handleDeleteClick,
    handleDeleteAllTranscriptsClick,
    showTemplateDropdownInline,
    showEditTemplate,
    canEditTemplate,
    canRegenerate,
    showEditTemplateInline,
    editButtonLabel,
    handleEditTemplateClick,
    showCopyButtonInline,
    selectedSectionsCount,
    onCopyAllSections,
    onCopySelectedSections,
    onDeleteNote,
    onFindReplace,
    onCopyPasteSettings,
    noteMetrics,
    noteId,
    encounterId,
    encounterData?.can_delete_transcripts,
  ]);

  // Left section: Template controls
  const leftSection = (
    <>
      {/* TOC Button - leftmost, shown on xs/sm/md when inline TOC is hidden */}
      {showTOCButton && sections.length > 0 && (
        <NoteTOCButton
          sections={sections}
          containerSelector={containerSelector}
          onCopySelected={onCopySelectedSections}
          noteId={noteId}
          encounterId={encounterId}
        />
      )}

      {/* Template Dropdown - shown inline only on md/lg breakpoints */}
      {showTemplateDropdownInline && (
        <TemplateDropdown
          currentTemplateId={currentTemplateId}
          currentTemplateName={currentTemplateName}
          onTemplateSelect={handleTemplateSelect}
          noteId={noteId}
          encounterId={encounterId}
          canRegenerate={canRegenerate}
        />
      )}

      {/* Edit Template button - shown inline only on lg breakpoint */}
      {/* Requires both feature flag and note-specific permission */}
      {showEditTemplate && canEditTemplate === true && showEditTemplateInline && (
        <Button
          variant="text"
          onClick={handleEditTemplateClick}
          startIcon={<EditIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          {editButtonLabel}
        </Button>
      )}
    </>
  );

  // Right section: Copy actions + Review & Sign button
  const rightSection = (
    <>
      {/* Dynamic Copy button - shown inline only on md/lg breakpoints */}
      {showCopyButtonInline &&
        (selectedSectionsCount === 0 ? (
          /* Copy All Sections - shown when nothing selected */
          onCopyAllSections && (
            <Button
              variant="text"
              onClick={onCopyAllSections}
              startIcon={<ContentCopyIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Copy All Sections
            </Button>
          )
        ) : (
          /* Deselect All + Copy Selected - shown when items selected */
          <>
            {/* Deselect All button - LEFT of Copy Selected */}
            <Button
              variant="text"
              onClick={handleDeselectAll}
              startIcon={<DeselectIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Deselect All
            </Button>

            {/* Copy Selected button */}
            {onCopySelectedSections && (
              <Button
                variant="text"
                onClick={onCopySelectedSections}
                startIcon={<ContentCopyIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Copy Selected ({selectedSectionsCount})
              </Button>
            )}
          </>
        ))}

      {/* Sign Note / Complete Note button — opens billing popover in parent */}
      {onSignNote && (
        <Button
          variant="contained"
          onClick={(e) => onOpenBillingPopover?.(e.currentTarget)}
          disabled={isSigning}
          startIcon={<PenToolIcon />}
          endIcon={<ArrowDropDownIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          {isSigning
            ? 'Processing...'
            : hasNoteSigning && hasPatient
              ? 'Sign Note'
              : 'Complete Note'}
        </Button>
      )}

      {/* AI Chat Toggle button - shown on all screens */}
      <Tooltip title={isChatVisible ? 'Hide AI Chat' : 'Show AI Chat'}>
        <Badge
          badgeContent={!isChatVisible && isAuditEnabled && auditIssueCount > 0 ? auditIssueCount : 0}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              height: 18,
              minWidth: 18,
              padding: '0 4px',
            },
          }}
        >
          <IconButton
            onClick={handleToggleChat}
            size="medium"
            sx={{
              color: isChatVisible
                ? theme.palette.primary.contrastText
                : theme.palette.primary.main,
              bgcolor: isChatVisible ? theme.palette.primary.main : 'transparent',
              transition: 'background-color 0.2s ease, color 0.2s ease',
              '&:hover': {
                bgcolor: isChatVisible ? theme.palette.primary.dark : theme.palette.action.hover,
              },
            }}
          >
            <AutoAwesomeIcon />
          </IconButton>
        </Badge>
      </Tooltip>
    </>
  );

  return (
    <>
      <TopBarLayout
        ref={ref}
        left={leftSection}
        right={rightSection}
        menuItems={menuItems}
        onMenuOpen={handleMenuOpen}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        variant="warning"
        title="Delete Note?"
        message="This action cannot be undone. Are you sure you want to delete this note?"
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        loading={isDeleting}
      />

      {/* Template Switch Modal (for xs/sm breakpoints) */}
      <TemplateSwitchModal
        open={templateSwitchModalOpen}
        onClose={handleTemplateSwitchModalClose}
        currentTemplateName={currentTemplateName}
        currentTemplateId={currentTemplateId}
        onTemplateSelect={handleTemplateSelectFromModal}
        isRegenerating={isRegenerating}
      />

      {/* Template Switch Confirmation Dialog */}
      <ConfirmDialog
        open={showConfirmDialog}
        variant="warning"
        title="Switch Template?"
        message={
          <>
            <Typography variant="body1" gutterBottom>
              You are about to regenerate this note with a different template.
            </Typography>

            <Box
              sx={{
                my: 2,
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2">
                <strong>Current:</strong> {currentTemplate.name || 'Not specified'}
              </Typography>
              <Typography variant="body2">
                <strong>New:</strong> {selectedTemplate?.display_name || 'N/A'}
              </Typography>
            </Box>

            <Typography variant="body2" color="error">
              ⚠️ This will create a new note. Your current note will remain unchanged.
            </Typography>
          </>
        }
        onClose={handleCancelRegenerate}
        onConfirm={handleConfirmRegenerate}
        confirmText="Regenerate Note"
        loading={isRegenerating}
      />

      {/* Delete Transcripts Confirmation Dialog */}
      <ConfirmDialog
        open={deleteAllTranscriptsDialogOpen}
        variant="warning"
        title={`Delete ${(encounterData?.inputs?.length ?? 0) === 1 ? 'Transcript' : 'Transcripts'}?`}
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              This will permanently delete {(encounterData?.inputs?.length ?? 0) === 1 ? 'the transcript' : 'all transcripts'} from this encounter.
            </Typography>
            <Typography variant="body2" color="error" fontWeight={500}>
              This cannot be undone. Notes will no longer be able to be regenerated.
            </Typography>
          </Box>
        }
        onClose={handleCancelDeleteAllTranscripts}
        onConfirm={handleConfirmDeleteAllTranscripts}
        confirmText={`Delete ${(encounterData?.inputs?.length ?? 0) === 1 ? 'Transcript' : 'Transcripts'}`}
        cancelText="Cancel"
        loading={isDeletingAllTranscripts}
      />
    </>
  );
};
